import {useMemo, useState, useEffect} from 'react'
import {useTable, usePagination} from 'react-table'
import {useIntl, FormattedMessage} from 'react-intl'
import {KTIcon} from '../../../_metronic/helpers'
import {useParams, Link} from 'react-router-dom'
import {FindPodIdByCode, CheckPodAccess, FindFirstPodCode} from '../FindPodIdByPodCode'
import usePostApi from '../custom_hooks/usePostApi'
import {defaultProfileImage} from '../core'
import {PageTitle} from '../../../_metronic/layout/core'
import TeamMembersKnowMore from '../know_more_modals/TeamMembersKmMain'
import TeamMembersKM from '../know_more_modals/team_members_KM/TeamMembers'
import 'react-datepicker/dist/react-datepicker.css'
import DateFormatter from '../DateFormatter'
import PropTypes from 'prop-types'
import {Pagination} from 'react-bootstrap'
import {UseToast} from '../useToast'
import {LightOutlineButton} from '../../ui/LightOutlineButton'
import {PrimaryOutlineButton} from '../../ui/PrimaryOutlineButton'
import {useThemeMode} from '../../../_metronic/partials'
import CryptoJS from 'crypto-js'
import secureLocalStorage from 'react-secure-storage'

const secretKey = process.env.REACT_APP_REQUEST_SIGN_SECRET_KEY

const DataTable = ({columns, data, isLoading, fetchData, refresh}) => {
  const {mode} = useThemeMode()
  const havePodAccess = CheckPodAccess()
  const firstPodCode = FindFirstPodCode()
  const [showKM, setShowKM] = useState(false)
  const intl = useIntl()
  const {id} = useParams()
  const documents = [
    {
      title: `(${id})`,
      path: `/${id}/documents`,
      isSeparator: false,
      isActive: false,
    },
  ]

  const podId = FindPodIdByCode()
  const userInfo = secureLocalStorage.getItem('user_info')
  let user_id = ''
  let auth_token = ''
  if (userInfo) {
    ;({user_id, auth_token} = userInfo)
  }

  const payload = {
    pod_id: podId,
    user_id,
    auth_token,
    pod_code: id,
  }

  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), secretKey).toString()
  const encoded = encodeURIComponent(encrypted)

  const url = `${process.env.REACT_APP_REQUEST_SIGN_PATH}/request-signatures?data=${encoded}`

  const [inputVal, setInputVal] = useState('')
  const [currentStatus, setCurrentStatus] = useState('1')

  const [searchByRole, setSearchByRole] = useState(null)
  const filteredData = useMemo(() => {
    return data
      ? data.filter((each) => {
          const docTitle = each?.doc_title
          const status = each?.status?.toString()

          const docTitleMatches = docTitle?.toLowerCase()?.includes(inputVal?.toLowerCase())

          let statusMatches = false
          if (currentStatus === 'I') {
            statusMatches = status?.toLowerCase() === 'i'
          } else if (currentStatus === '1') {
            statusMatches = status?.toLowerCase() === '1'
          } else {
            statusMatches = status?.toLowerCase() !== 'i' && status?.toLowerCase() !== '1'
          }

          const roleIDMatches = searchByRole ? each.role_id === searchByRole : true

          // return docTitleMatches && roleIDMatches && statusMatches
          return docTitleMatches
        })
      : []
  }, [inputVal, data, searchByRole, currentStatus])

  const tableInstance = useTable({columns, data: filteredData}, usePagination)
  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    page,
    state: {pageIndex, pageSize},
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageCount,
    gotoPage,
    setPageSize,
  } = tableInstance

  const pageOptions = [10, 20, 30, 40, 50]

  const handleDifferentViews = () => {
    if (isLoading && refresh === false) {
      return (
        <div className='card h-250px'>
          <div className='m-auto d-flex flex-column align-items-center'>
            <div className='spinner-border spinner-primary mr-15'></div>
          </div>
        </div>
      )
    } else if (data && data.length > 0) {
      return (
        <div>
          {refresh ? (
            <div className='card h-250px'>
              <div className='m-auto d-flex flex-column align-items-center'>
                <div className='spinner-border spinner-primary mr-15'></div>
              </div>
            </div>
          ) : (
            <>
              <div className='card p-5 mb-5'>
                <div className='d-flex flex-column flex-md-row justify-content-md-between align-items-center border-0 px-0 mb-5'>
                  <h3>Drafts</h3>

                  <div className='d-flex flex-wrap gap-3'>
                    <div className='d-flex align-items-center position-relative'>
                      <KTIcon iconName='magnifier' className='fs-3 position-absolute ms-3' />
                      <input
                        type='search'
                        id='kt_filter_search'
                        className='form-control form-control-white form-control-sm w-150px ps-9 fw-normal'
                        placeholder={intl.formatMessage({id: 'LABEL.SEARCH'})}
                        value={inputVal}
                        onChange={(e) => setInputVal(e.target.value)}
                        autoComplete='off'
                      />
                    </div>

                    <a
                      href={url}
                      className={`btn btn-sm bg-${
                        mode === 'dark' ? 'secondary' : 'white'
                      } btn-outline border-primary text-primary btn-active-light-primary d-flex align-items-center`}
                    >
                      <KTIcon iconName='plus' className='fs-3 text-primary' />
                      Create Request
                    </a>
                    <LightOutlineButton
                      className='btn btn-sm btn-bg-light fw-normal px-3 pe-2 btn-active-primary'
                      onClick={() => fetchData(true)}
                    >
                      <i className='ki-solid ki-arrows-circle fs-3'></i>
                    </LightOutlineButton>
                  </div>
                </div>

                <div className='mb-0'>
                  <div className='overflow-auto'>
                    <table
                      className='table table-row-dashed table-row-gray-300 align-middle'
                      {...getTableProps()}
                    >
                      <thead>
                        <tr className='fw-semibold text-muted bg-light rounded'>
                          <th className='min-w-100px ps-3'>Request Title</th>
                          {/* <th className='min-w-150px'>Saved At</th> */}
                          <th className='min-w-150px text-center pe-3'>Actions</th>
                        </tr>
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {page.length > 0 ? (
                          page.map((row) => {
                            prepareRow(row)
                            return (
                              <tr {...row.getRowProps()}>
                                {row.cells.map((cell) => (
                                  <td className='text-center' {...cell.getCellProps()}>
                                    {cell.render('Cell')}
                                  </td>
                                ))}
                              </tr>
                            )
                          })
                        ) : (
                          <tr>
                            <td className='' colSpan={4}>
                              <div className='d-flex justify-content-center'>
                                <p className='text-dark default-cursor d-block fs-6 m-0 my-5 ps-3'>
                                  {inputVal === '' ? (
                                    intl.formatMessage({id: 'NO_RESULTS_FOUND_MSG'})
                                  ) : (
                                    <FormattedMessage
                                      id='MSG.NO_DATA_MATCH'
                                      values={{
                                        inputVal: <span className='text-primary'>{inputVal}</span>,
                                      }}
                                    />
                                  )}
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {filteredData.length > 10 && (
                <Pagination>
                  <Pagination.First onClick={() => gotoPage(0)} disabled={!canPreviousPage} />
                  <Pagination.Prev onClick={previousPage} disabled={!canPreviousPage} />

                  {[...Array(pageCount)].map((_, i) => (
                    <Pagination.Item key={i} active={i === pageIndex} onClick={() => gotoPage(i)}>
                      {i + 1}
                    </Pagination.Item>
                  ))}

                  <Pagination.Next onClick={nextPage} disabled={!canNextPage} />
                  <Pagination.Last
                    onClick={() => gotoPage(pageCount - 1)}
                    disabled={!canNextPage}
                  />

                  <div className='d-flex align-items-center mx-5'>
                    <span className='me-2'>{intl.formatMessage({id: 'BTN.SHOW'})}</span>
                    <select
                      className='form-select form-select-solid fw-bold w-75px me-2'
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      {pageOptions.map((option) => (
                        <option key={option} value={option} className='select-bg'>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </Pagination>
              )}
            </>
          )}
        </div>
      )
    } else {
      return (
        <div className='card text-center p-10'>
          <h4 className='fw-semibold mb-3'>No Draft Requests Found</h4>
          <p className='text-muted mb-4'>
            You haven’t started any document requests yet. Create a draft to begin the signing
            process.
          </p>
          <div className='d-flex justify-content-center'>
            {/* <PrimaryOutlineButton iconName='plus'>Create Request</PrimaryOutlineButton> */}
            <a
              href={url}
              className={`btn btn-sm bg-${
                mode === 'dark' ? 'secondary' : 'white'
              } btn-outline border-primary text-primary btn-active-light-primary d-flex align-items-center`}
            >
              <KTIcon iconName='plus' className='fs-3 text-primary' />
              Create Request
            </a>
          </div>
        </div>
      )
    }
  }

  return havePodAccess ? (
    <>
      <TeamMembersKnowMore showModal={showKM} setShowModal={setShowKM} />

      <PageTitle breadcrumbs={documents}>Documents</PageTitle>
      {handleDifferentViews()}
    </>
  ) : (
    <div className='card min-h-250px d-flex flex-column justify-content-center align-items-center bg-image'>
      <h1 className='fw-semibold fs-2hx text-gray-900 mb-4'>Oops!</h1>
      <div className='fs-6 text-gray-500 mb-7'>You don't have access to this POD.</div>
      <div className='mb-0'>
        <Link to={`/${firstPodCode}/dashboard`} className='btn btn-sm btn-light-primary'>
          Return Home
        </Link>
      </div>
    </div>
  )
}

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
      Cell: PropTypes.func.isRequired,
      disableSortBy: PropTypes.bool,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  fetchData: PropTypes.func.isRequired,
}

const DraftsTable = () => {
  const {id} = useParams()
  const {mode} = useThemeMode()
  const podId = FindPodIdByCode()
  const {execute, loading} = usePostApi()
  const [draftsData, setDraftsData] = useState([])
  const [refresh, setRefresh] = useState(false)

  const getDocuments = async (show) => {
    if (show) {
      setRefresh(true)
    }
    try {
      const url = '/document/draft/get'
      const formData = {
        pod_id: podId,
      }

      const response = await execute(url, 'POST', formData)
      setDraftsData(response.data)
      setRefresh(false)
      if (show) {
        return UseToast(
          response.status_message,
          {
            position: 'bottom-right',
            theme: 'colored',
            autoClose: true,
          },
          'success'
        )
      }
    } catch (error) {
      console.error('Error:', error)
      setRefresh(false)
      if (show) {
        return UseToast(
          error.response?.data?.status_message,
          {
            position: 'bottom-right',
            theme: 'colored',
            autoClose: true,
          },
          'error'
        )
      }
    }
  }

  useEffect(() => {
    if (podId) {
      getDocuments()
    }
  }, [podId])

  const userInfo = secureLocalStorage.getItem('user_info')
  let user_id = ''
  let auth_token = ''
  if (userInfo) {
    ;({user_id, auth_token} = userInfo)
  }

  const payload = {
    pod_id: podId,
    user_id,
    auth_token,
    pod_code: id,
  }

  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), secretKey).toString()
  const encoded = encodeURIComponent(encrypted)

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({row}) => {
          const {doc_title} = row.original
          return (
            <div className='d-flex align-items-center ps-3'>
              <div className='d-flex justify-content-start flex-column text-start'>
                <p className='text-dark fw-semibold text-hover-primary mb-1 fs-6 default-cursor'>
                  {doc_title || ''}
                </p>
              </div>
            </div>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({row}) => {
          const {doc_draft_id} = row.original

          const url = `${process.env.REACT_APP_REQUEST_SIGN_PATH}/draft/request/${doc_draft_id}?data=${encoded}`
          return (
            <div className='text-center'>
              <a
                href={url}
                target='_blank'
                className={`btn btn-sm bg-${
                  mode == 'dark' ? 'secondary' : 'white'
                } btn-outline border-primary text-primary btn-active-light-primary`}
                rel='noreferrer'
                type='application/pdf'
              >
                Open
              </a>
            </div>
          )
        },
      },
    ],
    []
  )

  return (
    <DataTable
      columns={columns}
      data={draftsData || []}
      isLoading={loading}
      fetchData={getDocuments}
      refresh={refresh}
    />
  )
}

export {DraftsTable}
